import { formatDollars } from '@fruitful/fruitils';
import {
    getApexRegistrationOwnershipLabel,
    type ImAccount,
    ReadableApexAccountRegistrationType,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import Clipboard from '@react-native-clipboard/clipboard';
import { useLocalSearchParams } from 'expo-router';
import { useMemo } from 'react';
import {
    BodyMediumRegular,
    CopyIcon,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    InfoRow,
    RowList,
    ScreenContainer,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';
import { LastUpdated } from 'smoothie/src/components/date/LastUpdated';

import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';

const DetailsTable = ({
    apexAccountDetails,
}: {
    apexAccountDetails: ImAccount;
}) => {
    const {
        icon,
        color,
        nickname,
        accountNumber,
        registrationType,
        apexPortfolio,
        apexRiskLevel,
        isRetirementAccount,
        contributionSummaries,
    } = apexAccountDetails;

    const toast = useToastController();

    const copy = (value: string) => {
        Clipboard.setString(value);
        toast.show('Copied!', { duration: 1000 });
    };

    const ownershipType = getApexRegistrationOwnershipLabel(registrationType);

    const limits = useMemo(() => {
        return contributionSummaries
            .sort((a, b) => {
                return (a?.taxYear ?? 0) - (b?.taxYear ?? 0);
            })
            .map((contributionSummary, index) => {
                return (
                    <YStack key={`value-${index}`}>
                        <InfoRow
                            value={formatDollars(
                                Number(
                                    contributionSummary.regularAmount?.value ??
                                        '0.00',
                                ),
                            )}
                            title={`Contributions for ${contributionSummary.taxYear}`}
                        />
                        {contributionSummary.contributionLimit?.value !=
                            null && (
                            <InfoRow
                                value={formatDollars(
                                    Number(
                                        contributionSummary.contributionLimit
                                            ?.value ?? '0.00',
                                    ),
                                )}
                                title={`Contribution limit for ${contributionSummary.taxYear}`}
                            />
                        )}
                        {contributionSummary.remainingContributionAllowed
                            ?.value != null && (
                            <InfoRow
                                value={formatDollars(
                                    Number(
                                        contributionSummary
                                            .remainingContributionAllowed
                                            ?.value ?? '0.00',
                                    ),
                                )}
                                title={`Remaining contribution for ${contributionSummary.taxYear}`}
                                isPrivate
                            />
                        )}
                    </YStack>
                );
            });
    }, []);

    const isSepIra =
        registrationType ===
        SupportedApexAccountRegistrationType.RegistrationIraSep;

    return (
        <RowList
            rowHeaderProps={{
                title: nickname,
                leftContentOverride: (
                    <FruitfulCustomIcon
                        icon={icon ?? FRUITFUL_ICON_KEY}
                        color={color ?? 'peach'}
                        useAltColors={true}
                        size="xs"
                    />
                ),
            }}
        >
            <InfoRow
                title="Account number"
                value={accountNumber}
                trailingIcon={CopyIcon}
                onPress={() => {
                    copy(accountNumber);
                }}
                isPrivate
            />
            <InfoRow
                title="Account type"
                value={ReadableApexAccountRegistrationType[registrationType]}
            />
            {ownershipType != null && (
                <InfoRow title="Ownership" value={ownershipType} />
            )}
            {apexPortfolio != null && apexRiskLevel != null && (
                <>
                    <InfoRow
                        title="Portfolio"
                        value={apexPortfolio}
                        isPrivate
                    />
                    <InfoRow
                        title="Risk level"
                        value={`${apexRiskLevel} out of 10`}
                        isPrivate
                    />
                </>
            )}
            {isRetirementAccount && !isSepIra && limits.length > 0 && limits}
        </RowList>
    );
};

export const ApexAccountDetails = () => {
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();
    const {
        isLoading,
        data: apexAccountDetails,
        dataUpdatedAt,
    } = useGetApexAccountDetails(apexAccountId);

    useTrackViewOnFocus('Investment/Splash');

    return (
        <ScreenContainer
            title="Account details"
            hasBackButton
            onFallbackRoute="/home/investment"
        >
            {isLoading ? (
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    height={200}
                >
                    <Spinner />
                </YStack>
            ) : apexAccountDetails == null ? (
                <YStack>
                    <BodyMediumRegular>
                        Account details are not available
                    </BodyMediumRegular>
                </YStack>
            ) : (
                <YStack gap={32}>
                    <DetailsTable apexAccountDetails={apexAccountDetails} />
                    <LastUpdated dataUpdatedAt={dataUpdatedAt} />
                </YStack>
            )}
        </ScreenContainer>
    );
};
